import React from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { ClosureEvolutionRow } from '../../../../types';

const formatMonthLabel = (month: string) => {
  const iso = month.slice(0, 10);
  const [y, m] = iso.split('-');
  return `${m}/${y}`;
};

type Props = {
  title: string;
  data: ClosureEvolutionRow[];
  loading: boolean;
};

const ClosureEvolutionCard = ({ title, data, loading }: Props) => {
  const xLabels = data.map(d => formatMonthLabel(d.month));
  const okSeries = data.map(d => d.pctOk);
  const nokSeries = data.map(d => d.pctNok);

  let body: React.ReactNode;

  if (loading) {
    body = (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (data.length === 0) {
    body = (
      <Typography variant="body2" color="text.secondary">
        No hay datos para el rango seleccionado.
      </Typography>
    );
  } else {
    body = (
      <>
        <Box sx={{ width: '100%', height: 320 }}>
          <LineChart
            xAxis={[{ scaleType: 'point', data: xLabels }]}
            series={[
              { data: okSeries, label: '% OK' },
              { data: nokSeries, label: '% NOK' },
            ]}
            yAxis={[{ min: 0, max: 100 }]}
            sx={{
              [`.${axisClasses.left} .${axisClasses.label}`]: { transform: 'translateX(-10px)' },
            }}
          />
        </Box>

        <Box mt={2} sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>
                  Mes
                </th>
                <th style={{ textAlign: 'right', padding: 8, borderBottom: '1px solid #eee' }}>
                  OK
                </th>
                <th style={{ textAlign: 'right', padding: 8, borderBottom: '1px solid #eee' }}>
                  ERR
                </th>
                <th style={{ textAlign: 'right', padding: 8, borderBottom: '1px solid #eee' }}>
                  % OK
                </th>
                <th style={{ textAlign: 'right', padding: 8, borderBottom: '1px solid #eee' }}>
                  % Not OK
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map(r => (
                <tr key={r.month}>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f3f3' }}>
                    {formatMonthLabel(r.month)}
                  </td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f3f3', textAlign: 'right' }}>
                    {r.localesOk}
                  </td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f3f3', textAlign: 'right' }}>
                    {r.localesErr}
                  </td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f3f3', textAlign: 'right' }}>
                    {r.pctOk}%
                  </td>
                  <td style={{ padding: 8, borderBottom: '1px solid #f3f3f3', textAlign: 'right' }}>
                    {r.pctNok}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </>
    );
  }

  return (
    <Card data-guide="dashboard-closure-evolution">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={2}>
          <Typography variant="h6">{title}</Typography>
        </Box>

        {body}
      </CardContent>
    </Card>
  );
};

export default ClosureEvolutionCard;
